import React, { useEffect, useRef, useState } from "react";
import SearchControls from "./SearchControls";
import TableHead from "./TableHead";

import TableFooter from "./TableFooter";
import TableBody from "./TableBody";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { scrollToBottom } from "../../redux/reducers/OTISReducer";

type TestTableProps = {
  data: any[];
  LoadingTest: boolean;
  setLoadingTest: (loading: boolean) => void;
  handleRemoveRow: (id: string) => void;
  formData: { [key: string]: any };
  setFormData: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
  render: boolean;
  setRender: (val: boolean) => void;
  addRow: boolean;
  callOtis: () => void;
  accountData: any;
  setAccountData: (data: any) => void;
  objectToUpdate: any;
  setObjectToUpdate: (obj: any) => void;

  // New Props
  handleAddRow: () => void;
  handleResetSeason: () => void;
  handleSaveAll: () => void;
  isSaving: boolean;
  setModal: (val: boolean) => void;
  eansLoading: boolean;
  accountsLoading: boolean;
  otisSegIsLoading: boolean;
  userRole: string[];
               setText: any;

};

const TestTable: React.FC<TestTableProps> = ({
  data,
  LoadingTest,
  setLoadingTest,
  handleRemoveRow,
  handleResetSeason,
  render,
  setRender,
  addRow,
  callOtis,
  accountData,
  setAccountData,
  objectToUpdate,
  setObjectToUpdate,
  // buttons props----------------------------------------------
  handleAddRow,
  handleSaveAll,
  isSaving,
  setModal,
  eansLoading,
  accountsLoading,
  otisSegIsLoading,
  userRole, formData, // Add formData
  setFormData,
               setText
 // Add setFormData
  // isLoading,
}) => {
  const [grandTotal, setGrandTotal] = useState<{ [key: string]: any }>({});
  const [headerHeight, setHeaderHeight] = useState(56);
  const [topScrollWidth, setTopScrollWidth] = useState(0);
  const [freezeOffsets, setFreezeOffsets] = useState({
    l1: 0,
    l2: 0,
    l3: 0,
    l4: 0,
    w12: 0,
  });
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const isLoading = eansLoading || accountsLoading || otisSegIsLoading;
  const tableRef = useRef<HTMLDivElement | null>(null);
  const tableBodyRef = useRef<HTMLTableElement | null>(null);
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  let scrollBottm = useSelector(
    (state: RootState) => state.OTISReducer.scrollBottm
  );

  useEffect(() => {
    if (!scrollBottm) return;
    // Delay so the newly added row is in the DOM, then scroll table and page to show it
    const t = setTimeout(() => {
      const mainDiv = document.querySelector(".main") as HTMLElement | null;
      if (mainDiv) {
        mainDiv.scrollTop = mainDiv.scrollHeight;
      }
      const lastRow = mainDiv?.querySelector("tbody.borderInputs tr:last-of-type") as HTMLElement | null;
      if (lastRow) {
        lastRow.scrollIntoView({ behavior: "smooth", block: "end" });
      }
      dispatch(scrollToBottom(false));
    }, 150);
    return () => clearTimeout(t);
  }, [scrollBottm, dispatch]);

  useEffect(() => {
    const syncTableWidth = () => {
      const table = tableBodyRef.current;
      const scroller = tableRef.current;
      if (!table || !scroller) return;
      const width = Math.max(table.scrollWidth, scroller.clientWidth);
      setTopScrollWidth(width);

      const headerCells = Array.from(table.querySelectorAll("thead th")) as HTMLTableCellElement[];
      const labels = [
        "Distribution Channel/Name",
        "Organization Name/ID",
        "INITIAL ORDERS",
        "LTD ORDERS",
      ];

      const matched = labels
        .map((label) => {
          const normalized = label.replace(/\s+/g, " ").trim();
          return headerCells.find((cell) => {
            const cellText = (cell.textContent || "").replace(/\s+/g, " ").trim();
            return !cell.classList.contains("hidden") && cellText === normalized;
          });
        })
        .filter((cell): cell is HTMLTableCellElement => Boolean(cell));

      if (matched.length === 4) {
        const [w1, w2, w3, w4] = matched.map((cell) => Math.max(cell.offsetWidth, 0));
        setFreezeOffsets({
          l1: 0,
          l2: w1,
          l3: w1 + w2,
          l4: w1 + w2 + w3,
          w12: w1 + w2,
        });
      }
    };

    syncTableWidth();

    const resizeObserver = new ResizeObserver(() => syncTableWidth());
    if (tableBodyRef.current) resizeObserver.observe(tableBodyRef.current);
    if (tableRef.current) resizeObserver.observe(tableRef.current);
    tableBodyRef.current?.querySelectorAll("thead th").forEach((th) => resizeObserver.observe(th));

    window.addEventListener("resize", syncTableWidth);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", syncTableWidth);
    };
  }, [data.length, addRow, formData, render]);

  const handleTableScroll = () => {
    if (!tableRef.current || !topScrollRef.current) return;
    topScrollRef.current.scrollLeft = tableRef.current.scrollLeft;
  };

  const handleTopScroll = () => {
    if (!tableRef.current || !topScrollRef.current) return;
    tableRef.current.scrollLeft = topScrollRef.current.scrollLeft;
  };

  // Measure thead height so the sticky totals row can sit directly below it
  useEffect(() => {
    const el = theadRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setHeaderHeight(el.offsetHeight);
    });
    ro.observe(el);
    setHeaderHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="p-4">
      {/* Search and Filter Controls----------------------------------- */}
      <SearchControls
        setLoadingTest={setLoadingTest}
        data={data}
        setFormData={setFormData}
        onRefetch={callOtis}
        userRole={userRole}
        addRow={addRow}
        handleAddRow={handleAddRow}
        handleRemoveRowAction={handleRemoveRow}
        handleSaveAll={handleSaveAll}
        isSaving={isSaving}
        setModal={setModal}
        isLoading={isLoading}
      />

      {/* Table Container------------------------------------- */}
      <div
        ref={topScrollRef}
        onScroll={handleTopScroll}
        className="overflow-x-auto overflow-y-hidden h-3 border border-gray-300 border-b-0 rounded-t-lg bg-gray-100"
        style={{ scrollbarWidth: "thin" }}
      >
        <div style={{ width: `${topScrollWidth}px`, minHeight: "1px" }} />
      </div>

      <div
        ref={tableRef}
        onScroll={handleTableScroll}
        className="overflow-x-auto max-h-[100vh] border border-gray-300 rounded-b-lg main"
        style={{
          ["--frz-l1" as any]: `${freezeOffsets.l1}px`,
          ["--frz-l2" as any]: `${freezeOffsets.l2}px`,
          ["--frz-l3" as any]: `${freezeOffsets.l3}px`,
          ["--frz-l4" as any]: `${freezeOffsets.l4}px`,
          ["--frz-w12" as any]: `${freezeOffsets.w12}px`,
        }}
      >
        <div className="inline-block min-w-full pr-2 align-top">
        <table ref={tableBodyRef} className="border border-gray-300 w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
          {/* Table Head-------------------------------------------- */}

          <TableHead ref={theadRef} />

          {/* Totals Row (at top, sticky below header)----------------------------------------------------- */}
          <TableFooter
            grandTotal={grandTotal}
            records={data}
            formData={formData}
            headerHeight={headerHeight}
          />

          {/* Table Body------------------------------------------------- */}
          <TableBody
            tableRef={tableRef}
            setLoadingTest={setLoadingTest}
            data={data}
            LoadingTest={LoadingTest}
            handleRemoveRow={handleRemoveRow}
            setGrandTotal={setGrandTotal} setModal={setModal} formData={formData} // Pass formData
            setFormData={setFormData} // Pass setFormData
             setText={setText}
          />
        </table>
        </div>
      </div>
    </div>
  );
};

export default TestTable;